import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { RecaptchaVerifier, signInWithPhoneNumber, linkWithPhoneNumber } from "firebase/auth";

const digitsOnly = (value) => String(value ?? "").replace(/\D+/g, "");

export const toE164TR = ({ dialCode = "+90", nationalNumber } = {}) => {
  const safeDial = String(dialCode ?? "").trim() || "+90";
  let national = digitsOnly(nationalNumber);
  if (national.startsWith("0")) national = national.slice(1);
  if (!national) return null;
  return `${safeDial}${national}`;
};

export const toFriendlyPhoneAuthError = (err) => {
  const code = err?.code ? String(err.code) : "";

  if (code.includes("auth/too-many-requests")) {
    return "Çok fazla deneme yapıldı. Lütfen bir süre bekleyip tekrar deneyin.";
  }
  if (code.includes("auth/invalid-app-credential")) {
    return "Uygulama kimliği doğrulanamadı (invalid-app-credential). Authorized domains, API key referrer kısıtları ve Phone provider ayarlarını kontrol edin.";
  }
  if (code.includes("auth/captcha-check-failed") || code.includes("captcha")) {
    return "reCAPTCHA doğrulaması başarısız oldu. Reklam engelleyicileri kapatıp tekrar deneyin.";
  }
  if (code.includes("auth/internal-error")) {
    return "SMS gönderilemedi (internal-error). Bu genelde API key kısıtları (HTTP referrer blocked), Identity Toolkit API kısıtları veya tarayıcıda reCAPTCHA script’inin engellenmesi nedeniyle olur.";
  }

  return "İşlem başarısız. Lütfen tekrar deneyin.";
};

export function usePhoneOtpFirebase({ auth, recaptchaButtonId = "sign-in-button", mode = "signIn", user = null } = {}) {
  const verifierRef = useRef(null);
  const renderPromiseRef = useRef(null);
  const confirmationRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [ready, setReady] = useState(false);

  const isClient = useMemo(() => typeof window !== "undefined" && typeof document !== "undefined", []);

  const clearRecaptcha = useCallback(() => {
    try {
      const widgetId = widgetIdRef.current;
      const grecaptcha = window?.grecaptcha;
      if (widgetId != null && typeof grecaptcha?.reset === "function") {
        grecaptcha.reset(widgetId);
      }
    } catch {}

    try {
      if (typeof document !== "undefined") {
        const frames = document.querySelectorAll(
          "iframe[title*='recaptcha' i], iframe[src*='recaptcha' i]"
        );
        frames.forEach((iframe) => {
          const title = String(iframe.getAttribute("title") || "").toLowerCase();
          if (title.includes("challenge") ||
          (title.includes("recaptcha") && iframe.style?.zIndex)) {
            iframe.remove();
          }
        });
      }
    } catch {}

    try {
      verifierRef.current?.clear?.();
    } catch {}
    verifierRef.current = null;
    renderPromiseRef.current = null;
    widgetIdRef.current = null;
    setReady(false);
  }, []);

  const initRecaptcha = useCallback(async () => {
    if (!auth) throw new Error("Firebase auth instance gerekli");
    if (!isClient) throw new Error("reCAPTCHA sadece tarayıcıda başlatılabilir");

    const el = document.getElementById(recaptchaButtonId);
    if (!el) {
      throw new Error(`reCAPTCHA hedef butonu bulunamadı: #${recaptchaButtonId}`);
    }

    if (!verifierRef.current) {
      verifierRef.current = new RecaptchaVerifier(auth, recaptchaButtonId, {
        size: "invisible",
        callback: () => {},
        "expired-callback": () => {
          renderPromiseRef.current = null;
          setReady(false);
        },
      });
    }

    if (!renderPromiseRef.current) {
      renderPromiseRef.current = verifierRef.current.render().then((widgetId) => {
        widgetIdRef.current = widgetId;
        setReady(true);
      });
    }

    await renderPromiseRef.current;
    return verifierRef.current;
  }, [auth, isClient, recaptchaButtonId]);

  const resetRecaptcha = useCallback(async () => {
    clearRecaptcha();
    await initRecaptcha();
  }, [clearRecaptcha, initRecaptcha]);

  const sendCode = useCallback(
    async (phoneE164) => {
      const verifier = await initRecaptcha();
      const safeMode = String(mode || "signIn").toLowerCase();
      const confirmation =
        safeMode === "link"
          ? await linkWithPhoneNumber(user || auth?.currentUser, phoneE164, verifier)
          : await signInWithPhoneNumber(auth, phoneE164, verifier);
      confirmationRef.current = confirmation;
      return confirmation;
    },
    [auth, initRecaptcha, mode, user]
  );

  const resendCode = useCallback(
    async (phoneE164) => {
      await resetRecaptcha();
      return sendCode(phoneE164);
    },
    [resetRecaptcha, sendCode]
  );

  const verifyCode = useCallback(async (otp) => {
    const confirmation = confirmationRef.current;
    if (!confirmation) {
      throw new Error("Önce SMS gönderin.");
    }
    const code = String(otp ?? "").trim();
    if (!code) {
      throw new Error("Lütfen SMS doğrulama kodunu girin.");
    }
    return confirmation.confirm(code);
  }, []);

  useEffect(() => {
    return () => {
      clearRecaptcha();
      confirmationRef.current = null;
    };
  }, [clearRecaptcha]);

  return {
    ready,
    initRecaptcha,
    resetRecaptcha,
    clearRecaptcha,
    sendCode,
    resendCode,
    verifyCode,
    getConfirmationResult: () => confirmationRef.current,
  };
}
